import { defineStore, getActivePinia } from 'pinia'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import type { TListDeleteRequestQueryParams, TListRequestQueryParams } from '@/types/api-types'
import { Pagination } from '@/models'
import {
  ListPagingTypeExpression,
  ListOrderingTypeExpression,
  ListFilterTypeExpression,
  DeleteTypeExpression
} from '@/enum/LinqExpressionType'
import { CANCELED_REQUEST_CODE, DEFAULT_PAGE_LIMIT, DEFAULT_PAGE_OFFSET } from '@/utils/constants'
import ListType from '@/enum/ListType'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import useGlobalStore from '../global'
import state from './state'
import getters from './getters'

type TRequestQueryParams = TListRequestQueryParams
type TDeleteRequestQueryParams = TListDeleteRequestQueryParams

const usePaginationStore = (name: string) => {
  const useStore = defineStore(name, {
    state: () => ({ ...state }),
    getters,
    actions: {
      async handleListAPICall<T, R = undefined>(
        api: (args: TRequestQueryParams, requestBody?: any) => TApiResponse,
        ListItemClass?: new (item: any) => T,
        explicitQueryExpressionOptions?: Record<
          string,
          | number
          | string
          | (string | number)[]
          | Record<string, number | string | (string | number)[]>
        >,
        requestBody?: any
      ) {
        try {
          let explicitQueries
          const filterQueries: any = {}
          const isFirstAPICall = this.offset === DEFAULT_PAGE_OFFSET

          if (
            explicitQueryExpressionOptions &&
            ListFilterTypeExpression.Filter in explicitQueryExpressionOptions
          ) {
            const { [ListFilterTypeExpression.Filter]: explicitFilterQueries, ...rest } =
              explicitQueryExpressionOptions
            explicitQueries = rest
            filterQueries[ListFilterTypeExpression.Filter] = {
              ...this.filtersOptions?.[ListFilterTypeExpression.Filter],
              ...(explicitFilterQueries as any)
            }
          } else {
            explicitQueries = explicitQueryExpressionOptions
          }

          const globalStore = useGlobalStore()
          const list = await api(
            {
              [ListPagingTypeExpression.Offset]: this.offset,
              [ListPagingTypeExpression.Limit]: Math.min(
                Math.max(
                  globalStore.validationConfig?.pagingLimit.default ?? this.limit,
                  this.limit
                ),
                globalStore.validationConfig?.pagingLimit.max ?? this.limit
              ),
              ...this.filtersOptions,
              ...filterQueries,
              ...explicitQueries
            },
            requestBody
          )

          if (list?.data) {
            const listData = new Pagination<T>(list.data)
            await this.updatePaginationState(listData, ListItemClass)
          }
          return list
        } catch (error: any) {
          if (error.code !== CANCELED_REQUEST_CODE) {
            this.failed = true
            ErrorHandlerService.handleNetworkError(error)
          }
        } finally {
          this.loading = false
        }
        return ''
      },
      async handleListAPICallFiltered<T, R = undefined>(
        api: (args: TRequestQueryParams, requestBody?: any) => TApiResponse,
        ListItemClass?: new (item: any) => T,
        filterOptions?: any,
        nonFilterQueryExpressionOptions?: Record<
          string,
          | number
          | string
          | (string | number)[]
          | Record<string, number | string | (string | number)[]>
        >,
        requestBody?: any
      ) {
        try {
          const filters = {
            [ListPagingTypeExpression.Offset]: this.offset,
            [ListPagingTypeExpression.Limit]: this.limit,
            ...filterOptions,
            ...nonFilterQueryExpressionOptions
          }

          const list = await api(filters, requestBody)

          if (list?.data) {
            const listData = new Pagination<T>(list.data)
            await this.updatePaginationState(listData, ListItemClass)
          }
        } catch (error: any) {
          this.failed = true
        } finally {
          this.loading = false
        }
        return ''
      },
      async handleListAPICallAndReassignSelectedItems<T, R = undefined>(
        api: (args: TRequestQueryParams, requestBody?: any) => TApiResponse,
        mapper?: (dto: T) => R | T,
        nonFilterQueryExpressionOptions?: Record<
          string,
          | number
          | string
          | (string | number)[]
          | Record<string, number | string | (string | number)[]>
        >,
        requestBody?: any,
        comparisonKey?: string
      ) {
        try {
          const filters = {
            [ListPagingTypeExpression.Offset]: this.offset,
            [ListPagingTypeExpression.Limit]: this.limit,
            ...this.filtersOptions,
            ...nonFilterQueryExpressionOptions
          }

          if (this.offset !== 0) {
            filters[ListPagingTypeExpression.Cookie] = this.cookie
          }

          if (filters[ListFilterTypeExpression.SearchText] && this.offset === 0) {
            this.cookie = null
            delete filters[ListPagingTypeExpression.Cookie]
          }

          const list = await api(filters, requestBody)

          if (list?.data) {
            const listData = new Pagination<T>(list.data)
            await this.updatePaginationStateAndReassignSelectedItems(
              listData,
              mapper,
              comparisonKey as keyof (T | R)
            )
          }
          return list
        } catch (error) {
          console.log(error)
          this.failed = true
          ErrorHandlerService.handleNetworkError(error)
        } finally {
          this.loading = false
        }
        return ''
      },
      async updatePaginationState<T, R>(data: Pagination<T>, ListItemClass?: new (item: any) => T) {
        this.offset += data.offset
        this.limit = data.limit
        this.totalRowCount = data.totalRowCount
        this.currentPageSize = data.currentPageSize

        const itemsInList = this.data?.map((item: any) => item.id)

        data.data?.forEach((item: any) => {
          const mappedDto: any = ListItemClass ? new ListItemClass(item) : item
          if (!this.data) {
            this.data = []
          }
          if (!itemsInList?.includes(item.id)) this.data.push(mappedDto)
        })
        this.noData = !this.size
      },
      async updatePaginationStateAndReassignSelectedItems<T, R>(
        data: Pagination<T>,
        mapper?: (dto: T) => R | T,
        comparisonKey?: keyof (T | R)
      ) {
        this.offset += data.offset
        this.limit = data.limit
        this.totalRowCount = data.totalRowCount
        this.currentPageSize = data.currentPageSize
        this.cookie = data.cookie
        data.data?.forEach((item: T) => {
          let mappedDto = mapper ? mapper(item) : item
          if (!this.data) {
            this.data = []
          }
          if (comparisonKey && this.selectedItems) {
            const selectedItemIndex = this.selectedItems.findIndex(
              (selectedItem: any) => mappedDto[comparisonKey] === selectedItem[comparisonKey]
            )
            if (selectedItemIndex !== undefined && selectedItemIndex > -1) {
              mappedDto = this.selectedItems[selectedItemIndex]
            }
          }
          this.data.push(mappedDto)
        })
        this.noData = !this.size
      },
      async refreshSelectedItemsAPICall<T, R = undefined>(
        api: (args: TRequestQueryParams) => TApiResponse,
        filterQueryExpressionOptions: TRequestQueryParams,
        ListItemClass?: new (dto: T) => R | T
      ) {
        try {
          this.loading = true
          const response = await api(filterQueryExpressionOptions)
          const otherItems = this.data?.filter(
            (item: any) => !this.selectedItems?.includes(item.id)
          )
          if (response.data.value) {
            const selectedItems = response.data.value.map((item: any) =>
              ListItemClass ? new ListItemClass(item) : item
            )
            if (this.data) {
              this.data = [...selectedItems, ...(otherItems ?? [])]
            }
          }
        } catch (error) {
          console.error(error)
        } finally {
          this.loading = false
        }
      },
      async handleListDeleteAPICall<T>(
        api: (args?: TDeleteRequestQueryParams, requestBody?: any) => TApiResponse,
        nonFilterQueryExpressionOptions?: Record<
          string,
          | number
          | string
          | (string | number)[]
          | Record<string, number | string | (string | number)[]>
        >
      ) {
        try {
          let deleteResponse
          if (
            nonFilterQueryExpressionOptions &&
            !!Object.keys(nonFilterQueryExpressionOptions).length
          ) {
            deleteResponse = await api(nonFilterQueryExpressionOptions)
          } else if (this.hasActiveFilters) {
            const filterParams: Record<string, string | number> = {}
            Object.keys(this.filtersOptions).forEach((key: string) => {
              const transformedKey = key.split('.')[1]
              filterParams[transformedKey] = this.filtersOptions[key]
            })
            deleteResponse = await api(filterParams)
          } else if (this.isSelectedAllSnackbarItems) {
            deleteResponse = await api()
          }
          return deleteResponse
        } catch (error) {
          console.log(error)
          this.failed = true
        } finally {
          this.loading = false
        }
        return null
      },
      async updateSelectedItems(items?: Array<any>) {
        if (items?.length) {
          this.selectedItems = items.map((item) => item)
        }
      },
      getDeleteQueryParams() {
        if (this.isSelectedAllSnackbarItems) {
          if (this.hasActiveFilters) {
            const filterParams: Record<string, any> = {}
            Object.keys(this.filtersOptions).forEach((key: string) => {
              if (key.includes('Paging')) return
              const transformedKey = key.split('.')[1]
              filterParams[transformedKey] = this.filtersOptions[key]
            })
            if (Object.keys(filterParams).length > 0) {
              return filterParams
            }
          }
          return undefined
        }
        if (this.someItemsSelected || this.allItemsSelected) {
          return { [DeleteTypeExpression.Expression]: { Id: this.selectedItems } }
        }
        return undefined
      },
      clearSelectedItems() {
        this.selectedItems = null
      },
      async toggleSelectAllItemsOfList(returnValue?: boolean, listType?: string, key = 'id') {
        if (listType === ListType.Assignment) return
        if (!this.isAllListItemsSelected) {
          this.isSelectedAllSnackbarItems = false
          this.selectedItems = []
          return
        }
        if (this.isAllListItemsSelected && this.data) {
          if (!this.allItemsSelected) {
            const itemsToBeSelected = this.data.filter(
              (item: any) => !this.selectedItems?.includes(item[key])
            )
            this.selectedItems = [
              ...(this.selectedItems ?? []),
              ...itemsToBeSelected.map((item: any) => {
                if (returnValue) return item
                return item[key]
              })
            ]
          }
        }
      },
      clearPaginationFilterOptions() {
        this.filtersOptions = {}
      },
      clearPaginationFilterExpressionOptions() {
        this.filtersOptions = {
          ...this.filtersOptions,
          [ListFilterTypeExpression.Filter]: {}
        }
      },
      setPaginationFilterOptions(filters?: any) {
        if (filters && Object.values(filters).length > 0) {
          this.filtersOptions = { ...this.filtersOptions, ...filters }
        } else {
          const { [ListFilterTypeExpression.Filter]: FilterExpressions, ...rest } =
            this.filtersOptions
          this.filtersOptions = rest
        }
      },
      setPaginationOrderingExpressionOptions(key: string, orderingExpressionOptions: string) {
        this.setPaginationFilterOptions({
          [ListOrderingTypeExpression.Ordering]: {
            [key]: orderingExpressionOptions
          }
        })
      },
      setPaginationFilterExpressionOptions(
        key: string,
        filterExpressionOptions?: string | number | boolean | (string | number | boolean)[]
      ) {
        const existingFilterExpressionOptions = this.filtersOptions[ListFilterTypeExpression.Filter]
        const removeOptionsFromFilterExpression =
          (this.filtersOptions[ListFilterTypeExpression.Filter] &&
            key in this.filtersOptions[ListFilterTypeExpression.Filter] &&
            !filterExpressionOptions) ||
          (Array.isArray(filterExpressionOptions) && !(filterExpressionOptions.length > 0))

        if (removeOptionsFromFilterExpression) {
          if (
            !this.filtersOptions[ListFilterTypeExpression.Filter] ||
            !(key in this.filtersOptions[ListFilterTypeExpression.Filter])
          )
            return
          const { [key]: tempKey, ...rest } = this.filtersOptions[ListFilterTypeExpression.Filter]

          if (Object.values(rest).length > 0) {
            this.setPaginationFilterOptions({
              [ListFilterTypeExpression.Filter]: rest
            })
          } else {
            this.setPaginationFilterOptions()
          }
        } else {
          this.setPaginationFilterOptions({
            [ListFilterTypeExpression.Filter]: {
              ...existingFilterExpressionOptions,
              [key]: filterExpressionOptions
            }
          })
        }
      },
      resetPaginationState() {
        this.$reset()
      },
      resetPaginationStateAndReassignSelection(key: string, temporarySelectedItems?: string[]) {
        const selectedItems = Array.isArray(this.selectedItems)
          ? [...(this.selectedItems ?? [])]
          : this.selectedItems
        const selectedData = this.data?.filter(
          (item: any) =>
            this.selectedItems?.includes(item[key]) || temporarySelectedItems?.includes(item[key])
        )
        this.resetPaginationState()
        if (selectedItems) {
          this.selectedItems = selectedItems
        }
        if (selectedData?.length) {
          this.data = [...selectedData]
        }
      },
      resetPaginationStateAndReassignTemporarySelection(
        key: string,
        temporarySelectedItems?: string[]
      ) {
        const existingSelectedItems = [...(this.selectedItems ?? [])]
        const unselectedExistingSelectedItems = this.selectedItems?.filter(
          (item: any) => !temporarySelectedItems?.includes(item)
        )
        const selectedItems = temporarySelectedItems?.length
          ? this.selectedItems?.filter((item: any) => temporarySelectedItems.includes(item))
          : []
        const selectedData = this.data?.filter((item: any) =>
          temporarySelectedItems?.includes(item[key])
        )
        this.$reset()
        this.selectedItems = existingSelectedItems
        if (selectedItems) {
          this.temporarySelectedItems = unselectedExistingSelectedItems ?? null
        }
        if (selectedData) {
          this.data = selectedData
          this.selectedItemsData = selectedData
        }
      },
      resetPaginationStateAndPreserveSelection() {
        const selectedItems = Array.isArray(this.selectedItems)
          ? [...(this.selectedItems ?? [])]
          : this.selectedItems
        this.resetPaginationState()
        if (selectedItems) {
          this.selectedItems = selectedItems
        }
      },
      resetPaginationStateAndNotFilters(preservePageOffset = false) {
        if (preservePageOffset) {
          this.offset = this.offset - this.currentPageSize
        } else {
          this.offset = DEFAULT_PAGE_OFFSET
          this.limit = DEFAULT_PAGE_LIMIT
        }
        this.responseRowCount = 0
        this.loading = true
        this.totalRowCount = 0
        this.noData = false
        this.data = []
        this.currentPageSize = 0
        this.selectedItems = null
        this.isAllListItemsSelected = false
        this.isSelectedAllSnackbarItems = false
        this.failed = false
      },
      resetPaginationWithPreserveFiltersAndSelection() {
        const selectedItems = [...(this.selectedItems ?? [])]
        this.resetPaginationStateAndNotFilters()
        if (selectedItems.length) {
          this.selectedItems = selectedItems
        }
      },
      resetAndDeletePaginationState() {
        const pinia = getActivePinia()
        this.$reset()
        this.$dispose()
        delete pinia?.state.value[this.$id]
      },
      toggleIsSelectedAllSnackbarItems() {
        this.isSelectedAllSnackbarItems = !this.isSelectedAllSnackbarItems
      },
      removeItemsInListFromSelectedItems(key = 'id') {
        const listItemsWhichNeedToBeRemovedFromSelectedItems = this.data
          ?.filter((item: any) => this.selectedItems?.includes(item[key]))
          .map((item: any) => item[key])
        const remainingSelectedItems = this.selectedItems?.filter(
          (key: string) => !listItemsWhichNeedToBeRemovedFromSelectedItems?.includes(key)
        )
        if (remainingSelectedItems) {
          this.selectedItems = remainingSelectedItems
        }
      },
      addData(data: any) {
        if (this.loading) {
          this.loading = false
        }
        if (!this.data) {
          this.data = [data]
        } else {
          this.data = [...this.data, data]
        }
      },
      addOrReplaceData(data: any) {
        if (!this.data) {
          this.data = [data]
        } else {
          const existingIndex = this.data.findIndex((item: any) => item.id === data.id)
          if (existingIndex > -1) {
            this.data.splice(existingIndex, 1, data)
          } else {
            this.data.push(data)
          }
        }
      },
      addOrReplaceMultipleData(data: any[]) {
        if (!this.data) {
          this.data = [data]
        } else {
          data.forEach((item) => {
            const existingIndex = this.data?.findIndex(
              (existingItem: any) => existingItem.id === item.id
            )
            if (existingIndex !== undefined && existingIndex > -1) {
              this.data?.splice(existingIndex, 1, item)
            } else if (Array.isArray(this.data)) {
              this.data.push(item)
            }
          })
        }
      }
      // async handleMockListAPICall<T, R = undefined>(
      //   api: (args: TRequestQueryParams, requestBody?: any) => TApiResponse,
      //   mapper?: (dto: T) => R | T,
      //   nonFilterQueryExpressionOptions?: Record<
      //     string,
      //     | number
      //     | string
      //     | (string | number)[]
      //     | Record<string, number | string | (string | number)[]>
      //   >,
      //   requestBody?: any
      // ) {
      //   try {
      //     const globalStore = useGlobalStore()
      //     const list = await api(
      //       {
      //         [ListPagingTypeExpression.Offset]: this.offset,
      //         [ListPagingTypeExpression.Limit]: Math.min(
      //           Math.max(
      //             globalStore.validationConfig?.pagingLimit.default ?? this.limit,
      //             this.limit
      //           ),
      //           globalStore.validationConfig?.pagingLimit.max ?? this.limit
      //         ),
      //         ...this.filtersOptions,
      //         ...nonFilterQueryExpressionOptions
      //       },
      //       requestBody
      //     )
      //     if (list?.data) {
      //       const listData = new Pagination<T>(list.data)
      //       await this.updatePaginationState(listData, mapper)
      //     }
      //     return list
      //   } catch (error: any) {
      //     if (error.code !== CANCELED_REQUEST_CODE) {
      //       this.failed = true
      //       ErrorHandlerService.handleNetworkError(error)
      //     }
      //   } finally {
      //     this.loading = false
      //   }
      //   return ''
      // }
    }
  })
  return useStore()
}

export default usePaginationStore
